/**
 * The wording the Hero Banner screen uses to say whether the banner is on the public events page.
 *
 * The spec requires that a staff user opening the screen on a hidden banner can tell it is not
 * currently on the public page. An unticked checkbox does not do that on its own: the form is
 * fully populated and every field looks exactly as it does for a live banner. A one-word status
 * chip beside the heading does — and reads faster than the paragraph this replaced.
 *
 * The saved value and the form value are kept apart on purpose. If the badge tracked the checkbox
 * alone it would start lying the instant someone ticked it — claiming a banner was back up when
 * nothing had been saved. So an unsaved change reads as what is true now plus the action that
 * would apply it: "Hidden · save to publish".
 */

export type HeroBannerVisibilityTone = "live" | "hidden" | "pending" | "unsaved";

export type HeroBannerVisibility = {
  tone: HeroBannerVisibilityTone;
  /** The status chip beside the "Visibility" heading. Short enough to read at a glance. */
  badge: string;
  /**
   * The one reassurance that makes hiding usable — that nothing is lost. Only ever shown while
   * the control is cleared; `null` means the screen renders no helper line at all.
   */
  hint: string | null;
};

/** Shown whenever the control is cleared, whatever is saved: hiding never destroys content. */
const KEPT_HINT = "Content is kept, so you can put it back later.";

export function describeHeroBannerVisibility({
  saved,
  pending,
}: {
  /** What content-api has stored. `null` when no banner has ever been saved. */
  saved: boolean | null;
  /** What the control on screen is set to, which is only stored once the form is saved. */
  pending: boolean;
}): HeroBannerVisibility {
  const hint = pending ? null : KEPT_HINT;

  if (saved === null) {
    return { tone: "unsaved", badge: "Not saved yet", hint };
  }

  if (saved && pending) {
    return { tone: "live", badge: "Live", hint };
  }

  if (saved && !pending) {
    return { tone: "pending", badge: "Live · save to hide", hint };
  }

  if (!saved && pending) {
    return { tone: "pending", badge: "Hidden · save to publish", hint };
  }

  return { tone: "hidden", badge: "Hidden", hint };
}
